import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from '../styles.module.scss';
import { removeUnicode } from '../utils/remove-unicode';
import { processUnicodeInElement } from './helpers/process-unicode';
import { checkForLimit } from './helpers/check-for-limit';
// ensures that the text is sliced correctly and doesn't break multi-unit characters
var cutOffExceedingLimit = function (input, limit) {
    if (!window.Intl || !Intl.Segmenter)
        return input.slice(0, limit);
    var segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    var graphemes = Array.from(segmenter.segment(input), function (s) { return s.segment; });
    return graphemes.slice(0, limit).join('');
};
export var SimpleEditor = function (_a) {
    var _b;
    var placeholder = _a.placeholder, disabled = _a.disabled, onChange = _a.onChange, editorCustomStyles = _a.editorCustomStyles, limit = _a.limit, _c = _a.allowEnters, allowEnters = _c === void 0 ? true : _c, displayAsMultiline = _a.displayAsMultiline, content = _a.content, onContentLengthChange = _a.onContentLengthChange, onFocus = _a.onFocus, onBlur = _a.onBlur, automationId = _a.automationId, stripUnicode = _a.stripUnicode;
    var editorRef = useRef(null);
    var _d = useState(false), isComposing = _d[0], setIsComposing = _d[1];
    var _e = useState(!content), isEmpty = _e[0], setIsEmpty = _e[1];
    useEffect(function () {
        if (editorRef.current && content !== undefined) {
            var currentText = editorRef.current.innerText || '';
            if (currentText !== content && content !== null) {
                editorRef.current.innerHTML = '';
                // Convert each line into a text node followed by a <br>
                var lines_1 = content.split('\n');
                lines_1.forEach(function (line, i) {
                    editorRef.current.appendChild(document.createTextNode(line));
                    if (i < lines_1.length - 1) {
                        editorRef.current.appendChild(document.createElement('br'));
                    }
                });
                setIsEmpty(!content.trim());
                onContentLengthChange(content.length);
            }
        }
    }, [content, onContentLengthChange]);
    var restoreCaretAt = function (el, offset) {
        var selection = window.getSelection();
        var range = document.createRange();
        var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        var currentOffset = 0;
        var node = null;
        var localOffset = 0;
        // if the first thing that user types is a forbidden char
        if (!el.textContent) {
            var lastChild = el.lastChild;
            if (lastChild) {
                range.setStartAfter(lastChild);
                range.collapse(true);
                selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
                selection === null || selection === void 0 ? void 0 : selection.addRange(range);
                return;
            }
        }
        while (walker.nextNode()) {
            var textNode = walker.currentNode;
            var nextOffset = currentOffset + textNode.length;
            if (offset <= nextOffset) {
                node = textNode;
                localOffset = offset - currentOffset;
                break;
            }
            currentOffset = nextOffset;
        }
        if (node) {
            range.setStart(node, localOffset);
            range.collapse(true);
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
        }
    };
    var handleKeyDown = function (e) {
        if (!allowEnters && e.key === 'Enter') {
            e.preventDefault();
        }
        if (limit) {
            checkForLimit(e, editorRef.current.innerText.length, limit);
        }
    };
    var handleInput = function () {
        if (isComposing)
            return;
        var el = editorRef.current;
        if (!el)
            return;
        var selection = window.getSelection();
        var caretOffset = 0;
        if (selection === null || selection === void 0 ? void 0 : selection.rangeCount) {
            var range = selection.getRangeAt(0);
            var preRange = range.cloneRange();
            preRange.selectNodeContents(el);
            preRange.setEnd(range.endContainer, range.endOffset);
            caretOffset = preRange.toString().length;
        }
        var newContent = '';
        el.childNodes.forEach(function (node) {
            if (node.nodeType === Node.TEXT_NODE) {
                newContent += node.textContent;
            }
            else if (node.nodeType === Node.ELEMENT_NODE && (node.nodeName === 'DIV' || node.nodeName === 'BR')) {
                newContent += '\n';
                newContent += node.innerText || '';
            }
        });
        var shouldRestoreCaret = false;
        // Logic to clear out the unicode & restore the correct caretOffset
        if (stripUnicode) {
            var _a = processUnicodeInElement(el, caretOffset, removeUnicode), modified = _a.modified, processedContent = _a.newContent, adjustedCaretOffset = _a.adjustedCaretOffset;
            if (modified) {
                newContent = processedContent;
                caretOffset = adjustedCaretOffset;
                shouldRestoreCaret = true;
            }
        }
        if (limit && newContent.length > limit) {
            newContent = cutOffExceedingLimit(newContent, limit);
            el.textContent = newContent;
            caretOffset = Math.min(caretOffset, newContent.length);
            shouldRestoreCaret = true;
        }
        if (!allowEnters) {
            var cleaned = newContent.replace(/[\n\r]/g, '');
            if (cleaned !== newContent) {
                newContent = cleaned;
                el.textContent = cleaned;
                caretOffset = Math.min(caretOffset, newContent.length);
                shouldRestoreCaret = true;
            }
        }
        if (shouldRestoreCaret) {
            restoreCaretAt(el, caretOffset);
        }
        setIsEmpty(!newContent.trim());
        onContentLengthChange(newContent.length);
        onChange === null || onChange === void 0 ? void 0 : onChange(newContent);
    };
    var handleCompositionStart = function () { return setIsComposing(true); };
    var handleCompositionEnd = function () {
        setIsComposing(false);
        handleInput(); // Re-apply logic after composed char is done
    };
    var handlePaste = function (e) {
        e.preventDefault();
        var text = e.clipboardData.getData('text/plain');
        if (stripUnicode) {
            text = removeUnicode(text);
        }
        var selection = window.getSelection();
        if (selection === null || selection === void 0 ? void 0 : selection.rangeCount) {
            var range = selection.getRangeAt(0);
            range.deleteContents();
            var textNode = document.createTextNode(text);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        // let the DOM update before calling input handler
        setTimeout(function () {
            handleInput();
        });
    };
    var editorClasses = classNames(styles['simple-editor'], (_b = {},
        _b[styles['is-empty']] = isEmpty,
        _b[styles['is-disabled']] = disabled,
        _b[styles['single-line']] = !allowEnters && !displayAsMultiline,
        _b));
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    _jsx("div", { ref: editorRef, contentEditable: !disabled, suppressContentEditableWarning: true, className: editorClasses, "data-placeholder": placeholder, style: editorCustomStyles, onInput: handleInput, onKeyDown: handleKeyDown, onFocus: onFocus, onBlur: onBlur, onPaste: handlePaste, onCompositionStart: handleCompositionStart, onCompositionEnd: handleCompositionEnd, "data-automation-id": automationId }));
};
